"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ArrowRight,
  Snowflake,
  Sun,
} from "lucide-react"
import {
  getMonthlyTasks,
  getCurrentMonthIndex,
  getMonthName,
  getSeasonLabel,
  type MonthlyTask,
} from "@/data/guide-monthly-tasks"

interface GuideQuickStartProps {
  grassKey: string
  season: "cool" | "warm"
}

export function GuideQuickStart({ grassKey, season }: GuideQuickStartProps) {
  const [tasks, setTasks] = useState<MonthlyTask[]>([])
  const [monthName, setMonthName] = useState("")
  const [seasonLabel, setSeasonLabel] = useState("")

  useEffect(() => {
    const monthIndex = getCurrentMonthIndex()
    setTasks(getMonthlyTasks(grassKey, monthIndex))
    setMonthName(getMonthName(monthIndex))
    setSeasonLabel(getSeasonLabel(monthIndex))
  }, [grassKey])

  if (tasks.length === 0) return null

  const isCool = season === "cool"

  return (
    <div className="mb-8 rounded-xl border-2 border-green-200 bg-linear-to-br from-green-50/80 to-emerald-50/60 overflow-hidden not-prose">
      {/* Header */}
      <div className="px-5 py-4 bg-linear-to-r from-green-600 to-emerald-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5" />
            <div>
              <h3 className="font-bold text-base">
                Your {monthName} Action Plan
              </h3>
              <p className="text-green-100 text-xs">
                What to do right now for your {grassKey} lawn
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${
              isCool
                ? "bg-sky-500/30 text-sky-100"
                : "bg-amber-500/30 text-amber-100"
            }`}
          >
            {isCool ? (
              <Snowflake className="w-3 h-3" />
            ) : (
              <Sun className="w-3 h-3" />
            )}
            {seasonLabel}
          </span>
        </div>
      </div>

      {/* Tasks */}
      <div className="px-5 py-4">
        <ul className="space-y-2.5">
          {tasks.map((task, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  task.priority === "high"
                    ? "text-green-600"
                    : task.priority === "medium"
                    ? "text-green-400"
                    : "text-gray-300"
                }`}
              />
              <div className="flex-1">
                <span
                  className={`text-sm leading-relaxed ${
                    task.priority === "high"
                      ? "text-gray-900 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {task.task}
                </span>
                {task.toolLink && (
                  <Link
                    href={task.toolLink}
                    className="inline-flex items-center gap-1 ml-2 text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Use tool
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Link to full calendar */}
        <a
          href="#seasonal-calendar"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700 font-medium"
        >
          See full month-by-month calendar
          <ChevronDown className="w-4 h-4" />
        </a>
      </div>
    </div>
  )
}
